/**
 * Inventory Page
 * Vista para gestionar el inventario de materiales y equipamiento
 */

import { useState, useMemo } from "react";
import { AlertModal } from "@/components/ui/AlertModal/AlertModal";
import styles from "./InventoryPage.module.css";

interface Material {
  id: number;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  minStock: number;
  price: number;
  supplier: string;
  location: string;
  expiryDate: string | null;
  lastUpdated: string;
}

interface Movement {
  id: number;
  materialId: number;
  materialName: string;
  type: "in" | "out";
  quantity: number;
  reason: string;
  user: string;
  date: string;
}

type StockStatus = "ok" | "low" | "out";
type ViewMode = "materials" | "movements";

const InventoryPage = () => {
  const [materials, setMaterials] = useState<Material[]>([
    {
      id: 1,
      name: "Insecticida Premium",
      category: "Insecticidas",
      quantity: 45,
      unit: "litros",
      minStock: 20,
      price: 25.5,
      supplier: "QuímicaPro S.L.",
      location: "Almacén A - Estante 3",
      expiryDate: "2026-12-31",
      lastUpdated: "2026-01-15",
    },
    {
      id: 2,
      name: "Raticida en Bloque",
      category: "Rodenticidas",
      quantity: 8,
      unit: "kg",
      minStock: 15,
      price: 18.0,
      supplier: "Control Total",
      location: "Almacén A - Estante 1",
      expiryDate: "2027-06-30",
      lastUpdated: "2026-01-18",
    },
    {
      id: 3,
      name: "Gel Cucarachas",
      category: "Insecticidas",
      quantity: 32,
      unit: "tubos",
      minStock: 10,
      price: 12.75,
      supplier: "QuímicaPro S.L.",
      location: "Almacén A - Estante 3",
      expiryDate: "2026-08-15",
      lastUpdated: "2026-01-10",
    },
    {
      id: 4,
      name: "Trampas Adhesivas",
      category: "Trampas",
      quantity: 150,
      unit: "unidades",
      minStock: 50,
      price: 1.5,
      supplier: "SupplyPest",
      location: "Almacén B - Estante 2",
      expiryDate: null,
      lastUpdated: "2026-01-20",
    },
    {
      id: 5,
      name: "Fumigador Eléctrico",
      category: "Equipamiento",
      quantity: 3,
      unit: "unidades",
      minStock: 2,
      price: 350.0,
      supplier: "TechPest",
      location: "Almacén C - Zona Equipos",
      expiryDate: null,
      lastUpdated: "2026-01-05",
    },
    {
      id: 6,
      name: "Cebo Hormigas",
      category: "Insecticidas",
      quantity: 65,
      unit: "cajas",
      minStock: 25,
      price: 8.5,
      supplier: "QuímicaPro S.L.",
      location: "Almacén A - Estante 2",
      expiryDate: "2026-10-20",
      lastUpdated: "2026-01-19",
    },
    {
      id: 7,
      name: "Pulverizador Manual",
      category: "Equipamiento",
      quantity: 12,
      unit: "unidades",
      minStock: 5,
      price: 45.0,
      supplier: "TechPest",
      location: "Almacén C - Zona Equipos",
      expiryDate: null,
      lastUpdated: "2026-01-12",
    },
    {
      id: 8,
      name: "Desinfectante Superficies",
      category: "Desinfectantes",
      quantity: 28,
      unit: "litros",
      minStock: 20,
      price: 15.0,
      supplier: "HygienePlus",
      location: "Almacén A - Estante 4",
      expiryDate: "2027-03-31",
      lastUpdated: "2026-01-16",
    },
    {
      id: 9,
      name: "Guantes Nitrilo",
      category: "Equipamiento",
      quantity: 5,
      unit: "cajas",
      minStock: 10,
      price: 22.0,
      supplier: "SafetyPro",
      location: "Almacén B - Estante 1",
      expiryDate: "2028-12-31",
      lastUpdated: "2026-01-14",
    },
    {
      id: 10,
      name: "Líquido Mosquitos",
      category: "Insecticidas",
      quantity: 18,
      unit: "litros",
      minStock: 15,
      price: 28.0,
      supplier: "QuímicaPro S.L.",
      location: "Almacén A - Estante 3",
      expiryDate: "2026-09-30",
      lastUpdated: "2026-01-17",
    },
  ]);

  const [movements, setMovements] = useState<Movement[]>([
    {
      id: 1,
      materialId: 1,
      materialName: "Insecticida Premium",
      type: "out",
      quantity: 5,
      reason: "Orden de trabajo OT-000002",
      user: "Juan Pérez",
      date: "2026-01-21T09:30:00",
    },
    {
      id: 2,
      materialId: 3,
      materialName: "Gel Cucarachas",
      type: "in",
      quantity: 20,
      reason: "Compra - Factura #1234",
      user: "Admin",
      date: "2026-01-20T14:15:00",
    },
    {
      id: 3,
      materialId: 2,
      materialName: "Raticida en Bloque",
      type: "out",
      quantity: 3,
      reason: "Orden de trabajo OT-000001",
      user: "María García",
      date: "2026-01-19T11:00:00",
    },
    {
      id: 4,
      materialId: 4,
      materialName: "Trampas Adhesivas",
      type: "out",
      quantity: 25,
      reason: "Orden de trabajo OT-000003",
      user: "Carlos Rodríguez",
      date: "2026-01-18T16:45:00",
    },
    {
      id: 5,
      materialId: 8,
      materialName: "Desinfectante Superficies",
      type: "in",
      quantity: 15,
      reason: "Compra - Factura #1230",
      user: "Admin",
      date: "2026-01-17T10:30:00",
    },
  ]);

  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [viewMode, setViewMode] = useState<ViewMode>("materials");

  const categories = [
    "Insecticidas",
    "Rodenticidas",
    "Trampas",
    "Equipamiento",
    "Desinfectantes",
  ];

  const [formData, setFormData] = useState({
    name: "",
    category: "Insecticidas",
    quantity: "",
    unit: "litros",
    minStock: "",
    price: "",
    supplier: "",
    location: "",
    expiryDate: "",
  });

  const [movementForm, setMovementForm] = useState({
    materialId: "",
    type: "in" as "in" | "out",
    quantity: "",
    reason: "",
  });

  const filteredMaterials = useMemo(() => {
    return materials.filter((material) => {
      const matchesSearch =
        material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.location.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        categoryFilter === "all" || material.category === categoryFilter;

      let matchesStock = true;
      if (stockFilter === "low") {
        matchesStock = material.quantity <= material.minStock;
      } else if (stockFilter === "ok") {
        matchesStock = material.quantity > material.minStock;
      }

      return matchesSearch && matchesCategory && matchesStock;
    });
  }, [materials, searchTerm, categoryFilter, stockFilter]);

  const lowStockCount = materials.filter(
    (m) => m.quantity <= m.minStock,
  ).length;
  const totalValue = materials.reduce(
    (sum, m) => sum + m.quantity * m.price,
    0,
  );

  const handleOpenModal = (material: Material | null = null) => {
    if (material) {
      setFormData({
        name: material.name,
        category: material.category,
        quantity: material.quantity.toString(),
        unit: material.unit,
        minStock: material.minStock.toString(),
        price: material.price.toString(),
        supplier: material.supplier,
        location: material.location,
        expiryDate: material.expiryDate || "",
      });
      setSelectedMaterial(material);
    } else {
      setFormData({
        name: "",
        category: "Insecticidas",
        quantity: "",
        unit: "litros",
        minStock: "",
        price: "",
        supplier: "",
        location: "",
        expiryDate: "",
      });
      setSelectedMaterial(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMaterial(null);
  };

  const handleSaveMaterial = () => {
    if (
      !formData.name ||
      !formData.quantity ||
      !formData.minStock ||
      !formData.price
    ) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, completa todos los campos obligatorios",
        type: "warning",
      });
      return;
    }

    const materialData = {
      name: formData.name,
      category: formData.category,
      quantity: parseFloat(formData.quantity),
      unit: formData.unit,
      minStock: parseFloat(formData.minStock),
      price: parseFloat(formData.price),
      supplier: formData.supplier,
      location: formData.location,
      expiryDate: formData.expiryDate || null,
      lastUpdated: new Date().toISOString().split("T")[0],
    };

    if (selectedMaterial) {
      setMaterials(
        materials.map((m) =>
          m.id === selectedMaterial.id ? { ...m, ...materialData } : m,
        ),
      );
    } else {
      const newMaterial: Material = {
        id: Math.max(...materials.map((m) => m.id)) + 1,
        ...materialData,
      };
      setMaterials([...materials, newMaterial]);
    }

    handleCloseModal();
  };

  const handleDeleteMaterial = (id: number) => {
    if (confirm("¿Estás seguro de que quieres eliminar este material?")) {
      setMaterials(materials.filter((m) => m.id !== id));
      if (selectedMaterial?.id === id) {
        setSelectedMaterial(null);
      }
    }
  };

  const handleOpenMovementModal = () => {
    setMovementForm({
      materialId: materials[0]?.id.toString() || "",
      type: "in",
      quantity: "",
      reason: "",
    });
    setShowMovementModal(true);
  };

  const handleSaveMovement = () => {
    if (
      !movementForm.materialId ||
      !movementForm.quantity ||
      !movementForm.reason
    ) {
      setAlertModal({
        isOpen: true,
        message: "Por favor, completa todos los campos",
        type: "warning",
      });
      return;
    }

    const material = materials.find(
      (m) => m.id === parseInt(movementForm.materialId),
    );
    if (!material) return;

    const quantity = parseFloat(movementForm.quantity);

    if (movementForm.type === "out" && material.quantity < quantity) {
      setAlertModal({
        isOpen: true,
        message: "No hay suficiente stock para esta salida",
        type: "error",
      });
      return;
    }

    // Actualizar stock del material
    setMaterials(
      materials.map((m) =>
        m.id === parseInt(movementForm.materialId)
          ? {
              ...m,
              quantity:
                movementForm.type === "in"
                  ? m.quantity + quantity
                  : m.quantity - quantity,
              lastUpdated: new Date().toISOString().split("T")[0],
            }
          : m,
      ),
    );

    // Registrar movimiento
    const newMovement: Movement = {
      id: Math.max(...movements.map((m) => m.id), 0) + 1,
      materialId: parseInt(movementForm.materialId),
      materialName: material.name,
      type: movementForm.type,
      quantity: quantity,
      reason: movementForm.reason,
      user: "Usuario Actual", // En producción vendría de la sesión
      date: new Date().toISOString(),
    };

    setMovements([newMovement, ...movements]);
    setShowMovementModal(false);
  };

  const getStockStatus = (material: Material): StockStatus => {
    if (material.quantity === 0) return "out";
    if (material.quantity <= material.minStock) return "low";
    return "ok";
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>📦 Inventario</h1>
          <div className={styles.stats}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total Materiales</span>
              <span className={styles.statValue}>{materials.length}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Stock Bajo</span>
              <span
                className={`${styles.statValue} ${lowStockCount > 0 ? styles.warning : ""}`}
              >
                {lowStockCount}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Valor Total</span>
              <span className={styles.statValue}>{totalValue.toFixed(2)}€</span>
            </div>
          </div>
        </div>
        <button className={styles.addButton} onClick={() => handleOpenModal()}>
          + Nuevo Material
        </button>
      </div>

      <div className={styles.viewToggle}>
        <button
          className={`${styles.toggleButton} ${viewMode === "materials" ? styles.active : ""}`}
          onClick={() => setViewMode("materials")}
        >
          📦 Materiales
        </button>
        <button
          className={`${styles.toggleButton} ${viewMode === "movements" ? styles.active : ""}`}
          onClick={() => setViewMode("movements")}
        >
          📋 Movimientos
        </button>
      </div>

      {viewMode === "materials" ? (
        <>
          <div className={styles.controls}>
            <div className={styles.searchBar}>
              <input
                type="text"
                placeholder="🔍 Buscar material, proveedor o ubicación..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles.filters}>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
              >
                <option value="all">Todo el stock</option>
                <option value="low">Stock bajo</option>
                <option value="ok">Stock normal</option>
              </select>
            </div>
          </div>

          <div className={styles.materialsGrid}>
            {filteredMaterials.map((material) => {
              const stockStatus = getStockStatus(material);
              return (
                <div
                  key={material.id}
                  className={`${styles.materialCard} ${styles[stockStatus]}`}
                >
                  <div className={styles.cardHeader}>
                    <h3>{material.name}</h3>
                    <span className={styles.category}>{material.category}</span>
                  </div>
                  <div className={styles.stockInfo}>
                    <div className={styles.stockAmount}>
                      <span className={styles.quantity}>
                        {material.quantity} {material.unit}
                      </span>
                      {stockStatus === "low" && (
                        <span className={styles.lowStockBadge}>
                          ⚠️ Stock bajo
                        </span>
                      )}
                      {stockStatus === "out" && (
                        <span className={styles.outStockBadge}>
                          ❌ Sin stock
                        </span>
                      )}
                    </div>
                    <div className={styles.minStock}>
                      Mínimo: {material.minStock} {material.unit}
                    </div>
                  </div>
                  <div className={styles.cardDetails}>
                    <div className={styles.detailRow}>
                      <span>💰 Precio:</span>
                      <strong>
                        {material.price.toFixed(2)}€/{material.unit}
                      </strong>
                    </div>
                    <div className={styles.detailRow}>
                      <span>🏢 Proveedor:</span>
                      <span>{material.supplier}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span>📍 Ubicación:</span>
                      <span>{material.location}</span>
                    </div>
                    {material.expiryDate && (
                      <div className={styles.detailRow}>
                        <span>📅 Caducidad:</span>
                        <span>{formatDate(material.expiryDate)}</span>
                      </div>
                    )}
                    <div className={styles.detailRow}>
                      <span>🔄 Actualizado:</span>
                      <span>{formatDate(material.lastUpdated)}</span>
                    </div>
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      className={styles.editButton}
                      onClick={() => handleOpenModal(material)}
                    >
                      ✏️ Editar
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteMaterial(material.id)}
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className={styles.movementsSection}>
          <div className={styles.movementsHeader}>
            <h2>Historial de Movimientos</h2>
            <button
              className={styles.addButton}
              onClick={handleOpenMovementModal}
            >
              + Registrar Movimiento
            </button>
          </div>
          <div className={styles.movementsTable}>
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Material</th>
                  <th>Tipo</th>
                  <th>Cantidad</th>
                  <th>Motivo</th>
                  <th>Usuario</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id}>
                    <td>{formatDateTime(movement.date)}</td>
                    <td>{movement.materialName}</td>
                    <td>
                      <span
                        className={`${styles.movementType} ${styles[movement.type]}`}
                      >
                        {movement.type === "in" ? "⬇️ Entrada" : "⬆️ Salida"}
                      </span>
                    </td>
                    <td
                      className={
                        movement.type === "in"
                          ? styles.positive
                          : styles.negative
                      }
                    >
                      {movement.type === "in" ? "+" : "-"}
                      {movement.quantity}
                    </td>
                    <td>{movement.reason}</td>
                    <td>{movement.user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className={styles.modal} onClick={handleCloseModal}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>
                {selectedMaterial ? "✏️ Editar Material" : "➕ Nuevo Material"}
              </h2>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Ej: Insecticida Premium"
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Categoría *</label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Unidad *</label>
                  <select
                    value={formData.unit}
                    onChange={(e) =>
                      setFormData({ ...formData, unit: e.target.value })
                    }
                  >
                    <option value="litros">Litros</option>
                    <option value="kg">Kilogramos</option>
                    <option value="unidades">Unidades</option>
                    <option value="cajas">Cajas</option>
                    <option value="tubos">Tubos</option>
                  </select>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Cantidad Actual *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({ ...formData, quantity: e.target.value })
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Stock Mínimo *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.minStock}
                    onChange={(e) =>
                      setFormData({ ...formData, minStock: e.target.value })
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Precio/Unidad *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Proveedor</label>
                <input
                  type="text"
                  value={formData.supplier}
                  onChange={(e) =>
                    setFormData({ ...formData, supplier: e.target.value })
                  }
                  placeholder="Ej: QuímicaPro S.L."
                />
              </div>
              <div className={styles.formGroup}>
                <label>Ubicación en Almacén</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  placeholder="Ej: Almacén A - Estante 3"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Fecha de Caducidad</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) =>
                    setFormData({ ...formData, expiryDate: e.target.value })
                  }
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={handleCloseModal}
              >
                Cancelar
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSaveMaterial}
              >
                {selectedMaterial ? "Guardar Cambios" : "Crear Material"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMovementModal && (
        <div
          className={styles.modal}
          onClick={() => setShowMovementModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>📋 Registrar Movimiento</h2>
              <button
                className={styles.closeButton}
                onClick={() => setShowMovementModal(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Material *</label>
                <select
                  value={movementForm.materialId}
                  onChange={(e) =>
                    setMovementForm({
                      ...movementForm,
                      materialId: e.target.value,
                    })
                  }
                >
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} (Stock: {m.quantity} {m.unit})
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Tipo de Movimiento *</label>
                <select
                  value={movementForm.type}
                  onChange={(e) =>
                    setMovementForm({
                      ...movementForm,
                      type: e.target.value as "in" | "out",
                    })
                  }
                >
                  <option value="in">⬇️ Entrada (añadir stock)</option>
                  <option value="out">⬆️ Salida (restar stock)</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Cantidad *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={movementForm.quantity}
                  onChange={(e) =>
                    setMovementForm({
                      ...movementForm,
                      quantity: e.target.value,
                    })
                  }
                  placeholder="Cantidad a añadir o restar"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Motivo *</label>
                <input
                  type="text"
                  value={movementForm.reason}
                  onChange={(e) =>
                    setMovementForm({ ...movementForm, reason: e.target.value })
                  }
                  placeholder="Ej: Compra - Factura #1234 o Orden de trabajo OT-000123"
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowMovementModal(false)}
              >
                Cancelar
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSaveMovement}
              >
                Registrar Movimiento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AlertModal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
};

export default InventoryPage;
